// frontend_app/src/services/api.js

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const fetchWithAuth = async (url, options = {}) => {
  const { suppressAuthEvent = false, signal, ...fetchOptions } = options;
  const defaultOptions = {
    credentials: 'include',
    ...(signal ? { signal } : {}),
    ...fetchOptions,
    headers: {
      ...fetchOptions.headers,
      'X-LSearch-Requested-With': 'XMLHttpRequest',
    },
  };
  const response = await fetch(url, defaultOptions);
  if (response.status === 401 && !suppressAuthEvent) {
    window.dispatchEvent(new CustomEvent('lsearch-auth-expired'));
  }
  return response;
};

// --- Authentication Functions ---
export const login = async (username, password) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
    suppressAuthEvent: true,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Login failed' }));
    throw new Error(errorData.detail);
  }
};

export const logout = async () => {
  await fetchWithAuth(`${API_BASE_URL}/logout`, { method: 'POST' });
};

export const verify = async () => {
  const response = await fetchWithAuth(`${API_BASE_URL}/verify`);
  if (!response.ok) {
    throw new Error('Session is not valid');
  }
  return response.json();
};

// --- Application API Functions ---
export const streamSearch = (term, enabledSources, searchMode, enabledCustomFolders, isDeepSearch, onData, onDone, onError) => {
  const params = new URLSearchParams({
    query: term,
    search_mode: searchMode,
  });

  // Live searches will always use the cache by default on the backend
  if (searchMode === 'live') {
    params.append('use_cache', 'true');
  }

  if (enabledSources && enabledSources.length > 0) {
    params.append('sources', enabledSources.join(','));
  }
  if (enabledCustomFolders && enabledCustomFolders.length > 0) {
    params.append('custom_folders', enabledCustomFolders.join(','));
  }
  // Add the deep_search parameter if it's enabled
  if (isDeepSearch) {
    params.append('deep_search', 'true');
  }

  const url = `${API_BASE_URL}/stream-federated-search?${params.toString()}`;
  const eventSource = new EventSource(url, { withCredentials: true });
  eventSource.onmessage = (event) => { if (!event.data) return; try { const resultPackage = JSON.parse(event.data); onData(resultPackage); } catch (e) { console.error("Failed to parse event data:", event.data, e); } };
  eventSource.addEventListener('done', () => { onDone(); eventSource.close(); });
  eventSource.onerror = (err) => { onError("A streaming connection error occurred."); eventSource.close(); };
  return eventSource;
};

export const fetchSourcesConfig = async (signal) => {
  const apiUrl = `${API_BASE_URL}/sources`;
  const response = await fetchWithAuth(apiUrl, { signal });
  if (!response.ok) throw new Error("Failed to fetch source configuration.");
  const data = await response.json();
  return data.sources || [];
};

export const fetchSourcesStatus = async (signal) => {
  const apiUrl = `${API_BASE_URL}/sources-status`;
  const response = await fetchWithAuth(apiUrl, { signal });
  if (!response.ok) throw new Error("Failed to fetch sources status.");
  const data = await response.json();
  return data.sources_status || [];
};

export const fetchDetails = async (url, source, signal) => {
  const params = new URLSearchParams({ url: url, source: source });
  const apiUrl = `${API_BASE_URL}/fetch-details?${params.toString()}`;
  const response = await fetchWithAuth(apiUrl, { signal });
  if (!response.ok) {
    let errorDetail = `Failed to fetch details. Status: ${response.status}`;
    try {
      const errorData = await response.json();
      errorDetail = errorData.detail || errorDetail;
    } catch (e) {
      /* Ignore */
    }
    throw new Error(errorDetail);
  }
  const data = await response.json();
  return data.details || {};
};

export const fetchSuggestions = async (term) => {
  const params = new URLSearchParams({ q: term });
  const apiUrl = `${API_BASE_URL}/suggest?${params.toString()}`;
  const response = await fetchWithAuth(apiUrl);
  if (!response.ok) throw new Error("Failed to fetch suggestions.");
  const data = await response.json();
  return data.suggestions || [];
};

// --- Admin API Functions ---
export const fetchBuilders = async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/admin/builders`);
    if (!response.ok) throw new Error('Could not fetch builder list.');
    return response.json();
};
export const runBuilderScript = async (builderName) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/admin/run-builder/${builderName}`, { method: 'POST' });
    if (!response.ok) { const errorData = await response.json().catch(() => ({ detail: 'Failed to start builder script.' })); throw new Error(errorData.detail); }
    return response.json();
};

// --- User Management API ---
export const fetchUsers = async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/admin/users`);
    if (!response.ok) throw new Error('Could not fetch users.');
    return response.json();
};

export const addUser = async (userData) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to add user.' }));
        throw new Error(errorData.detail);
    }
    return response.json();
};

export const updateUser = async (userId, updateData) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update user.' }));
        throw new Error(errorData.detail);
    }
    return response.json();
};

export const deleteUser = async (userId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to delete user.' }));
        throw new Error(errorData.detail);
    }
    return response.json();
};

// --- Admin Scheduler Functions ---
export const fetchSchedules = async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/admin/schedules`);
    if (!response.ok) throw new Error('Could not fetch schedules.');
    return response.json();
};
export const updateSchedule = async (builderName, intervalDays) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/admin/schedule/${builderName}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interval_days: intervalDays }),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update schedule.' }));
        throw new Error(errorData.detail);
    }
    return response.json();
};